/**
 * Helpers para resolver o `tipo_programa` do usuário logado.
 *
 * O `tipo_programa` mora no JWT e representa o programa da empresa:
 *   - 0 → TCE (Estágio)
 *   - 1 → TCA (Aprendizagem) — fluxo padrão, sem etapa extra do estudante
 *   - 2 → TCA Empregador (Aprendizagem CIEEMG Empregador)
 *
 * Para `0` e `2`, o fluxo do Acompanhamento ganha uma etapa extra
 * "Preenchimento do Estudante" entre "Cadastro inicial" e
 * "Validação do preenchimento".
 *
 * Para CIEE/consultor (sem `tipo_programa` definido), assume-se o
 * programa padrão (1) para preservar o comportamento atual.
 */

import { jwtDecode } from "jwt-decode";

export const PROGRAMA_TCE = 0;
export const PROGRAMA_TCA = 1;
export const PROGRAMA_TCA_EMPREGADOR = 2;

/** Tipos de programa que exigem etapa "Preenchimento do Estudante". */
export const PROGRAMA_REQUER_ESTUDANTE = new Set([
  PROGRAMA_TCE,
  PROGRAMA_TCA_EMPREGADOR,
]);

/**
 * Lê o `tipo_programa` do usuário logado a partir do JWT.
 * Retorna 1 (TCA) como padrão quando não há token, é CIEE/consultor
 * ou ocorre qualquer erro de decodificação — mantendo o comportamento
 * atual do stepper para esses perfis.
 */
export function obterTipoProgramaUsuarioLogado() {
  if (typeof window === "undefined") return PROGRAMA_TCA;
  const token = window.localStorage.getItem("token");
  if (!token) return PROGRAMA_TCA;
  try {
    const decoded = jwtDecode(token);
    const bruto = decoded?.tipo_programa;
    const numero = Number(bruto);
    if (!Number.isFinite(numero)) return PROGRAMA_TCA;
    return numero;
  } catch {
    return PROGRAMA_TCA;
  }
}

/**
 * Indica se o programa informado exige a etapa extra do estudante
 * no stepper do Acompanhamento.
 */
export function programaRequerEtapaEstudante(tipoPrograma) {
  return PROGRAMA_REQUER_ESTUDANTE.has(Number(tipoPrograma));
}

/**
 * Resolve o rótulo do programa a partir do `tipo_programa`.
 * Retorna null quando o tipo é desconhecido.
 *
 *   0 → TCE        (Estágio)
 *   1 → TCA        (Aprendizagem)
 *   2 → TCA Emp.   (Aprendizagem)
 *
 * Aceita fallback pelo tamanho do documento:
 *   - 14 dígitos (CNPJ) → Aprendizagem
 *   - 11 dígitos (CPF)  → Estágio
 */
export function resolverIdentificacaoPrograma(item, documento) {
  const tp = Number(item?.tipo_programa ?? item?.tipoPrograma);
  if (Number.isFinite(tp)) {
    if (tp === PROGRAMA_TCE) {
      return { tipo: "ESTAGIO", rotulo: "Estágio", classe: "acomp-tp-estagio" };
    }
    if (tp === PROGRAMA_TCA || tp === PROGRAMA_TCA_EMPREGADOR) {
      return {
        tipo: "APRENDIZAGEM",
        rotulo: tp === PROGRAMA_TCA_EMPREGADOR ? "Aprendizagem (TCA Emp.)" : "Aprendizagem",
        classe: "acomp-tp-aprendizagem",
      };
    }
  }
  const nums = String(documento || "").replace(/\D/g, "");
  if (nums.length === 14) {
    return {
      tipo: "APRENDIZAGEM",
      rotulo: "Aprendizagem",
      classe: "acomp-tp-aprendizagem",
    };
  }
  if (nums.length === 11) {
    return {
      tipo: "ESTAGIO",
      rotulo: "Estágio",
      classe: "acomp-tp-estagio",
    };
  }
  return null;
}
